<template>
  <div class="relative">
    <component
      :is="uiStore.isMobile && mediaList.length > 1 ? Swiper:'div'"
      class="w-full mobOnly:overflow-hidden grid-cols-2 gap-24 md:mb-32"
      :slides-per-view="1.2"
      :space-between="4"
      :class="{
        'bg-image-bg': mediaList.length === 1,
        'md:grid': mediaList.length > 1
      }"
    >
      <component
        :is="uiStore.isMobile && mediaList.length > 1 ? SwiperSlide:'div'"
        v-for="(media, index) in mediaList"
        :key="media.src"
        class="w-full mobOnly:overflow-hidden cursor-zoom"
        :class="{
          'md:w-1/2 mx-auto': mediaList.length === 1,
          'w-full': mediaList.length > 1
        }"
        @click="toggleOverlay(index)"
      >
        <nuxt-img
          v-if="media.type === 'image'"
          :src="media.src"
          preset="standard"
          sizes="mob414:100vw desk:396px"
          :alt="product.title"
        />
        <video
          v-else-if="media.type === 'video'"
          class="object-cover h-full w-full"
          height="224"
          width="224"
          autoplay
          muted
          loop
          playsinline
        >
          <source :src="media.src" type="video/mp4">
        </video>
      </component>
      <div
        v-if="product.seal"
        class="flex items-center justify-center rounded-full h-80 w-80 absolute bottom-50 right-24 translate-y-24 md:top-auto md:bottom-0 md:-right-16 md:translate-y-16 transform bg-white z-above "
        :class="{ 'md:bottom-1/2': hasMultipleMediaItemRows }"
      >
        <div v-if="product.seal && product.seal[sealName]">
          <img
            :src="product.seal[sealName].url"
            class="h-full w-full absolute top-0 left-0"
            :alt="sealName"
          >
        </div>
      </div>
    </component>

    <!-- * * * * *  zoomed -->
    <div
      id="fullsizeMediaList"
      class="
        fixed
        h-screen
        w-screen
        bg-image-bg
        z-leave-page-overlay
        top-0
        left-0
        overflow-y-auto
      "
      :class="[showOverlay ? 'block' : 'hidden']"
    >
      <button
        class="fixed w-48 h-48 rounded-full bg-secondary flex right-40 top-32"
        @click.prevent="toggleOverlay()"
      >
        <fa
          class="flex-auto text-primary self-center justify-self-center"
          :icon="['far', 'times']"
        />
      </button>
      <div
        v-for="(media, index) in fullsizeMediaList"
        :key="media.src"
        class="block"
        :class="{ 'pointer-events-none': media.type === 'image' }"
      >
        <nuxt-img
          v-if="media.type === 'image'"
          :id="`media_${index}`"
          preset="standard"
          sizes="mob414:100vw desk:100vw"
          :src="media.src"
          :alt="product.title"
          class="h-auto max-w-screen-lg mx-auto min-w-1/2"
          itemprop="image"
        />
        <video
          v-else-if="media.type === 'video'"
          :id="`media_${index}`"
          class="h-auto max-w-screen-lg mx-auto"
          itemprop="image"
          controls
          playsinline
        >
          <source :src="media.src" type="video/mp4">
        </video>
      </div>
      <div
        v-if="product.seal"
        class="flex items-center justify-center rounded-full h-80 w-80 absolute bottom-50 right-24 translate-y-24 md:top-auto md:bottom-0 md:-right-16 md:translate-y-16 transform bg-white z-above "
        :class="{ 'md:bottom-1/2': hasMultipleMediaItemRows }"
      >
        <div v-if="product.seal && product.seal[sealName]">
          <img
            :src="product.seal[sealName].url"
            class="h-full w-full absolute top-0 left-0"
            :alt="sealName"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUiStore } from '~/store/ui';
import type Product from '~/models/product';
import { scrollTo } from '~/util/scroll-to';
import { useConfigStore } from '~/store/config';
const config = useConfigStore();

const uiStore = useUiStore();
const Swiper = resolveComponent('Swiper');
const SwiperSlide = resolveComponent('SwiperSlide');

const props = defineProps<{
  product: Product,
}>();

const mediaList = computed(()=> {
  const list = props.product.productImages.map((imageObj) => ({
    src: imageObj,
    type: 'image',
  }));
  if (props.product.videoUrl) {
    return [
      list[0],
      { src: props.product.videoUrl, type: 'video' },
      ...list.slice(1),
    ];
  } else {
    return list;
  }
});

const hasMultipleMediaItemRows = computed(()=> {
  return Object.keys(mediaList.value).length > 2;
});

const fullsizeMediaList = computed(()=>{
  const list = props.product.productImagesVeryLargesize.map((imageObj) => ({
    src: imageObj,
    type: 'image',
  }));
  if (props.product.videoUrl) {
    return [
      list[0],
      { src: props.product.videoUrl, type: 'video' },
      ...list.slice(1),
    ];
  } else {
    return list;
  }
});

const showOverlay = ref(false);
const toggleOverlay = (index = -1) => {
  showOverlay.value = !showOverlay.value;
  if (showOverlay.value && index > -1) {
    setTimeout(() => {
      scrollTo(
        document.getElementById(`media_${index}`),
        document.getElementById('fullsizeMediaList')
      );
    }, 250);
  }
  if (showOverlay.value) {
    document.body.classList.add('locked');
  } else {
    document.body.classList.remove('locked');
  }
};

const sealName = computed(()=> {
  return `${config.market.language.toLowerCase()}_img`;
});

</script>

<style scoped lang="postcss"></style>
